import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useState } from 'react'
import { MediaAsset } from '../../views'
import { useImageCacheWriter } from '../images'
import {
	feedAdImageHistoryAtom,
	feedAdImageIdAtom,
	feedAdMetaBackdropOverlayProModeAtom,
	feedAdMetaBackdropTransformAtom,
	feedAdMetaOverlayAtom,
	quickImageGenerateCallbackAtom,
	quickImageUploadCallbackAtom,
} from '../state'
import { trackImageEdited, trackImageUploaded } from '../tracking'
import { ImageFilmstrip } from './image-filmstrip'
import { OverlayEditor } from './shared'

export function FeedAdImageWidget() {
	const onQuickUpload = useAtomValue(quickImageUploadCallbackAtom)
	const onQuickGenerate = useAtomValue(quickImageGenerateCallbackAtom)
	const assets = useAtomValue(feedAdImageHistoryAtom)
	const { cacheImageUrl } = useImageCacheWriter()

	const [feedAdImageId, setFeedAdImageId] = useAtom(feedAdImageIdAtom)
	const setBackdropTransform = useSetAtom(feedAdMetaBackdropTransformAtom)

	const handleImageSelect = useCallback((asset: MediaAsset) => {
		setFeedAdImageId(asset.id)
		cacheImageUrl(asset.id, asset.url)
		// Reset backdrop transform based on collection
		setBackdropTransform((prev) => ({
			...prev,
			scale: asset.collection === 'generated' ? 1.2 : 1,
		}))
	}, [setFeedAdImageId, cacheImageUrl, setBackdropTransform])

	const [isQuickUploading, setIsQuickUploading] = useState(false)
	const handleQuickUpload = useCallback((file: File) => {
		if (!onQuickUpload) { return }
		setIsQuickUploading(true)
		onQuickUpload(
			file,
			(uuid, url) => {
				setFeedAdImageId(uuid)
				cacheImageUrl(uuid, url)
				// User-uploaded images are not in 'generated' collection, so use 1x scale
				setBackdropTransform((prev) => ({
					...prev,
					scale: 1,
				}))
				trackImageUploaded('feed')
				setIsQuickUploading(false)
			},
			() => {
				// Reset upload state on error so user can try again
				setIsQuickUploading(false)
			},
		)
	}, [onQuickUpload, setFeedAdImageId, cacheImageUrl, setBackdropTransform])

	const handleQuickGenerate = useCallback((
		prompt: string,
		onComplete: () => void,
		onError?: (message: string) => void,
	) => {
		if (!onQuickGenerate) { return }
		console.log('handleQuickGenerate in widget', prompt)
		onQuickGenerate(prompt, (uuid) => {
			console.log(`Generated feed ad image ${uuid} with prompt "${prompt}"`)
			setFeedAdImageId(uuid)
			// Generated images should have 1.2x scale
			setBackdropTransform((prev) => ({
				...prev,
				scale: 1.2,
			}))
			onComplete()
		}, onError)
	}, [onQuickGenerate, setFeedAdImageId, setBackdropTransform])

	const [feedAdMetaOverlay, setFeedAdMetaOverlay] = useAtom(feedAdMetaOverlayAtom)
	const [feedAdMetaBackdropOverlayProMode, setFeedAdMetaBackdropOverlayProMode] = useAtom(
		feedAdMetaBackdropOverlayProModeAtom,
	)

	if (!feedAdMetaOverlay) {
		return null
	}

	return (
		<>
			<ImageFilmstrip
				assets={assets}
				onSelect={handleImageSelect}
				selectedAsset={feedAdImageId ?? ''}
				onQuickUpload={handleQuickUpload}
				onQuickGenerate={handleQuickGenerate}
				isQuickUploading={isQuickUploading}
				onImageAction={(action) => trackImageEdited(action, 'feed')}
			/>
			<OverlayEditor
				overlay={feedAdMetaOverlay}
				onChange={setFeedAdMetaOverlay}
				proMode={feedAdMetaBackdropOverlayProMode ?? false}
				onProModeChange={setFeedAdMetaBackdropOverlayProMode}
				simulatorType="feed"
			/>
		</>
	)
}
